import { BlogSection } from '@/types/blog';

export const kunst_des_liebens: BlogSection = {
  id: 'kunst-des-liebens',
  title: 'Die Kunst des Liebens: Vier Elemente',
  content: `
    <p>Fromm definiert Liebe nicht als Gefühl, sondern als <strong>aktive Sorge für das Leben und Wachstum dessen, was wir lieben</strong>.</p>

    <p>Das klingt abstrakt. Ist es aber nicht.</p>

    <div class="blog-card-grid">
      <div class="blog-feature-card">
        <div class="blog-feature-icon">
          <svg class="w-6 h-6 text-sage-600 dark:text-sage-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </div>
        <h4 class="font-semibold text-joerg-900 dark:text-joerg-100 mb-2">Fürsorge (Care)</h4>
        <p class="text-joerg-700 dark:text-joerg-300">Liebe zeigt sich darin, dass ich mich um das Wohl des anderen kümmere – nicht in Worten, sondern in Taten. Ich schaffe aktiv Raum für dein Wachstum.</p>
      </div>

      <div class="blog-feature-card">
        <div class="blog-feature-icon">
          <svg class="w-6 h-6 text-sage-600 dark:text-sage-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        </div>
        <h4 class="font-semibold text-joerg-900 dark:text-joerg-100 mb-2">Verantwortung (Responsibility)</h4>
        <p class="text-joerg-700 dark:text-joerg-300">Nicht Kontrolle. Nicht Pflicht. Sondern: Response-ability – die Fähigkeit zu antworten. „Was brauchst du von mir?" statt „Du musst..."</p>
      </div>

      <div class="blog-feature-card">
        <div class="blog-feature-icon">
          <svg class="w-6 h-6 text-sage-600 dark:text-sage-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </div>
        <h4 class="font-semibold text-joerg-900 dark:text-joerg-100 mb-2">Respekt (Respect)</h4>
        <p class="text-joerg-700 dark:text-joerg-300">Von lateinisch <em>respicere</em>: „zurückschauen", „genau hinsehen." Den anderen sehen, wie er wirklich ist – nicht, wie ich ihn haben will. „Du darfst sein, wer du bist."</p>
      </div>

      <div class="blog-feature-card">
        <div class="blog-feature-icon">
          <svg class="w-6 h-6 text-sage-600 dark:text-sage-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <h4 class="font-semibold text-joerg-900 dark:text-joerg-100 mb-2">Erkenntnis (Knowledge)</h4>
        <p class="text-joerg-700 dark:text-joerg-300">Nicht oberflächliches Wissen. Sondern: Tiefes Verstehen. „Ich lerne dich jeden Tag neu kennen" statt „Ich kenne dich." Der einzige Weg, einen Menschen wirklich zu erkennen, ist liebevoll zu sein.</p>
      </div>
    </div>

    <div class="bg-gradient-to-br from-joerg-50 to-primary/10 dark:from-joerg-900 dark:to-primary/20 p-6 rounded-lg border border-joerg-200 dark:border-joerg-700 my-8">
      <h4 class="font-semibold text-joerg-800 dark:text-joerg-100 mb-3">💼 Business-Übersetzung</h4>
      <p class="text-joerg-700 dark:text-joerg-300 mb-4">Diese vier Elemente sind keine Theorie – sie sind die Grundlage jeder echten Beziehung:</p>
      <ul class="space-y-2 text-joerg-700 dark:text-joerg-300 text-sm">
        <li>• <strong>Fürsorge:</strong> „Ich sorge dafür, dass meine Kunden wachsen" (nicht: „Ich will Kunden gewinnen")</li>
        <li>• <strong>Verantwortung:</strong> „Ich antworte auf Bedürfnisse" (nicht: „Ich muss Zahlen erfüllen")</li>
        <li>• <strong>Respekt:</strong> „Ich sehe meine Kunden, wie sie sind" (nicht: „Sie sollen sein, wie ich sie brauche")</li>
        <li>• <strong>Erkenntnis:</strong> „Ich lerne meinen Markt ständig neu kennen" (nicht: „Ich kenne meinen Markt")</li>
      </ul>
    </div>
  `
};
